import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI } from '@matix/api-client';
import {
  ReportToolbar, Field, DateTimeRange, fmt2, fmtMoney, fmtInt,
  todayFrom, todayTo, rangeParams, rangeLabel, useLabelOf, printableHtml,
} from './reportShared';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel } from './reportExcel';
import { useLang } from '../../lib/language';

export function SalesBySalesmanReport(): JSX.Element {
  const { t, lang } = useLang();
  const labelOf = useLabelOf();
  const printRef = useRef<HTMLDivElement>(null);
  const [salesmanId, setSalesmanId] = useState<number | ''>('');
  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [params, setParams] = useState<
    { from: string; to: string; from_time?: string; to_time?: string; salesman_id?: number } | null
  >(null);

  const filtersQ = useQuery({ queryKey: ['rpt-filters'], queryFn: () => reportsAPI.filters() });

  const dataQ = useQuery({
    queryKey: ['sales-by-salesman', params],
    queryFn: () => reportsAPI.salesBySalesman(params!),
    enabled: params != null,
  });

  const range = rangeLabel(from, to);
  const title = t({ en: 'Sales by Salesman Report', ar: 'تقرير المبيعات حسب المندوب' });

  function run(): void {
    const r = rangeParams(from, to);
    if (!r) return;
    setParams({ ...r, ...(salesmanId !== '' ? { salesman_id: salesmanId } : {}) });
  }
  function exportPdf(paper: ReportPaper): void { printReport(title, range, printableHtml(printRef), paper); }

  const d = dataQ.data;

  function exportExcel(): void {
    if (!d) return;
    const sheet = new ReportSheetBuilder()
      .title(title, range)
      .table(
        [
          { label: t({ en: 'Salesman', ar: 'مندوب المبيعات' }) },
          { label: t({ en: 'Orders', ar: 'الطلبات' }), kind: 'int' },
          { label: t({ en: 'Services', ar: 'الخدمات' }), kind: 'qty' },
          { label: t({ en: 'Gross Sales', ar: 'إجمالي المبيعات' }), kind: 'money' },
          { label: t({ en: 'Returns', ar: 'المرتجعات' }), kind: 'money' },
          { label: t({ en: 'Discount', ar: 'الخصم' }), kind: 'money' },
          { label: t({ en: 'Net', ar: 'الصافي' }), kind: 'money' },
        ],
        d.rows.map((r) => [
          r.name || r.name_ar || `#${r.salesman_id}`,
          r.invoice_count, r.services_count, r.total_sales,
          r.total_returns, r.total_discount, r.net_total,
        ]),
        {
          emptyText: t({ en: 'No data', ar: 'لا توجد بيانات' }),
          totals: d.rows.length === 0 ? undefined : [
            t({ en: 'Total', ar: 'الإجمالي' }),
            d.totals.invoice_count, d.totals.services_count, d.totals.total_sales,
            d.totals.total_returns, d.totals.total_discount, d.totals.net_total,
          ],
        },
      )
      .build(title, { rightToLeft: lang === 'ar' });
    downloadReportExcel(`${title} ${range}`, sheet);
  }

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching}
        canExport={!!d}
        onRun={run}
        onExport={exportPdf}
        onExportExcel={exportExcel}
        filters={
          <>
            <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
            <Field label={t({ en: 'Salesman', ar: 'مندوب المبيعات' })}>
              <select value={salesmanId} onChange={(e) => setSalesmanId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">{t({ en: '— All —', ar: '— الكل —' })}</option>
                {filtersQ.data?.salesmen.map((s) => <option key={s.id} value={s.id}>{labelOf(s)}</option>)}
              </select>
            </Field>
          </>
        }
      />

      {dataQ.error && <div className="rpt-error">{t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}</div>}

      <div className="rpt-print" ref={printRef}>
        {d && (
          <>
            <div className="rpt-meta">{range}</div>
            <table>
              <thead>
                <tr>
                  <th>{t({ en: 'Salesman', ar: 'مندوب المبيعات' })}</th><th className="num">{t({ en: 'Orders', ar: 'الطلبات' })}</th><th className="num">{t({ en: 'Services', ar: 'الخدمات' })}</th>
                  <th className="num">{t({ en: 'Gross Sales', ar: 'إجمالي المبيعات' })}</th><th className="num">{t({ en: 'Returns', ar: 'المرتجعات' })}</th>
                  <th className="num">{t({ en: 'Discount', ar: 'الخصم' })}</th><th className="num">{t({ en: 'Net', ar: 'الصافي' })}</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.length === 0 && <tr><td colSpan={7} className="rpt-empty">{t({ en: 'No data', ar: 'لا توجد بيانات' })}</td></tr>}
                {d.rows.map((r) => (
                  <tr key={r.salesman_id}>
                    <td>{r.name || r.name_ar || `#${r.salesman_id}`}</td>
                    <td className="num">{fmtInt(r.invoice_count)}</td>
                    <td className="num">{fmt2(r.services_count)}</td>
                    <td className="num">{fmtMoney(r.total_sales)}</td>
                    <td className="num">{fmtMoney(r.total_returns)}</td>
                    <td className="num">{fmtMoney(r.total_discount)}</td>
                    <td className="num">{fmtMoney(r.net_total)}</td>
                  </tr>
                ))}
              </tbody>
              {d.rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td>{t({ en: 'Total', ar: 'الإجمالي' })}</td>
                    <td className="num">{fmtInt(d.totals.invoice_count)}</td>
                    <td className="num">{fmt2(d.totals.services_count)}</td>
                    <td className="num">{fmtMoney(d.totals.total_sales)}</td>
                    <td className="num">{fmtMoney(d.totals.total_returns)}</td>
                    <td className="num">{fmtMoney(d.totals.total_discount)}</td>
                    <td className="num">{fmtMoney(d.totals.net_total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </>
        )}
        {!d && !dataQ.isFetching && <div className="rpt-empty">{t({ en: 'Choose a date and time range, then “Show report”.', ar: 'اختر التاريخ والوقت، ثم “عرض التقرير”.' })}</div>}
      </div>
    </div>
  );
}
